/**
 * LinguistPro — the payment modal's state machine.
 *
 * A pure reducer: no React, no DOM, no timers. The component owns the side
 * effects (fetching a payment id, loading PayFast's engine, the 100 s timer)
 * and reports what happened as events; this file decides what that means and
 * what the customer is told.
 *
 * Anything the table does not list returns the SAME state object, so a late
 * or duplicate event (a stale TIMEOUT after success, a second callback) is a
 * no-op rather than an error.
 */

import type { PaymentPlan } from './onsitePayment';
import {
  ERROR_FALLBACK,
  EARLIER_PAYMENT_ALSO_PAID,
  PREVIOUS_ATTEMPT_UNFINISHED,
  SIGN_IN_AGAIN,
  FAILED,
  beforeMessage,
  errorMessage,
  rateLimitedMessage,
  successMessage,
  unconfirmedMessage,
} from './messages';

/** The single timeout constant; the component arms it from `generating` onward and clears it on every exit. */
export { ONSITE_CALLBACK_TIMEOUT_MS } from './onsitePayment';

/**
 * `failed` is reserved for a payment PayFast itself reports as failed; no
 * event produces it yet, but it has copy and actions so reaching it is safe.
 */
export type PaymentStatus =
  | 'idle'
  | 'generating'
  | 'engine_loading'
  | 'awaiting_payfast'
  | 'success'
  | 'failed'
  | 'cancelled'
  | 'timed_out'
  | 'rate_limited'
  | 'error';

export type PaymentAction = 'retry' | 'close' | 'contact_support';

export interface PaymentState {
  status: PaymentStatus;
  /** True when this attempt started while an earlier one was still unresolved. */
  supersededPrevious: boolean;
  /**
   * An attempt reached PayFast and no real callback (or return URL) has said
   * how it ended. Independent of what is on screen, so it survives CLOSE.
   */
  unresolvedAttempt: boolean;
  /**
   * `cancelled` / `timed_out` came from OUR guess (window gone or silent),
   * not from PayFast. A real paid callback may still follow and must win.
   */
  guessed: boolean;
  /** `cancelled` came from PayFast's cancel_url redirect: nothing was charged. */
  fromReturn: boolean;
  /**
   * An earlier attempt's real paid callback arrived after this attempt's
   * PayFast window had opened: the customer may have paid twice and is told.
   */
  earlierPaid: boolean;
  /** The generate request was refused as unauthenticated. */
  needsSignIn: boolean;
  plan: PaymentPlan | null;
  /** The pack price in rand — the credit value; a coupon lowers the price paid, not this. */
  amount: number | null;
  uuid: string | null;
  /** Seconds, from a 429. */
  retryAfter: number | null;
  /** The server's own error text, when it sent one. */
  message: string | null;
}

export type PaymentEvent =
  | { type: 'OPEN'; plan?: PaymentPlan; amount?: number }
  | { type: 'GENERATE_OK'; uuid: string; amount: number }
  | { type: 'GENERATE_ERR'; status: number; retryAfter?: number; message?: string }
  | { type: 'ENGINE_READY' }
  | { type: 'CALLBACK'; result: boolean }
  | { type: 'PAYFAST_CANCEL' }
  | { type: 'TIMEOUT' }
  /**
   * A superseded attempt's real PayFast callback arrived. `plan` and `amount`
   * are that attempt's own, used when it is the one shown as paid.
   */
  | { type: 'PRIOR_ATTEMPT_RESOLVED'; paid: boolean; plan?: PaymentPlan; amount?: number }
  | { type: 'RETURN_QUERY'; payment: 'success' | 'cancelled' }
  | { type: 'RESET' }
  | { type: 'CLOSE' };

export const INITIAL_PAYMENT_STATE: PaymentState = {
  status: 'idle',
  supersededPrevious: false,
  unresolvedAttempt: false,
  guessed: false,
  fromReturn: false,
  earlierPaid: false,
  needsSignIn: false,
  plan: null,
  amount: null,
  uuid: null,
  retryAfter: null,
  message: null,
};

/** States in which a payment may still complete — PayFast can call back late. */
const LIVE: ReadonlySet<PaymentStatus> = new Set([
  'generating',
  'engine_loading',
  'awaiting_payfast',
  'timed_out',
]);

/**
 * PayFast may have been asked to open for the running attempt (its window is
 * open, or its outcome was only guessed or already shown), so it cannot be
 * stopped and its outcome must still be reported.
 */
export function payFastMayBeOpen(state: PaymentState): boolean {
  return (
    state.status === 'awaiting_payfast' ||
    state.status === 'timed_out' ||
    state.status === 'success' ||
    (state.status === 'cancelled' && state.guessed)
  );
}

export function paymentReducer(state: PaymentState, event: PaymentEvent): PaymentState {
  switch (event.type) {
    case 'OPEN': {
      // A start while one is already being set up must not restart it.
      if (state.status === 'generating' || state.status === 'engine_loading') return state;
      return {
        ...INITIAL_PAYMENT_STATE,
        status: 'generating',
        unresolvedAttempt: state.unresolvedAttempt,
        supersededPrevious: state.unresolvedAttempt,
        plan: event.plan ?? 'payg',
        amount: event.amount ?? null,
      };
    }

    case 'GENERATE_OK':
      if (state.status !== 'generating') return state;
      return { ...state, status: 'engine_loading', uuid: event.uuid, amount: event.amount };

    case 'GENERATE_ERR':
      if (state.status !== 'generating' && state.status !== 'engine_loading') return state;
      if (event.status === 429) {
        return { ...state, status: 'rate_limited', retryAfter: event.retryAfter ?? null };
      }
      if (event.status === 401) return { ...state, status: 'error', needsSignIn: true, message: null };
      return { ...state, status: 'error', message: event.message?.trim() || null };

    case 'ENGINE_READY':
      return state.status === 'engine_loading'
        ? { ...state, status: 'awaiting_payfast', unresolvedAttempt: true }
        : state;

    case 'CALLBACK': {
      // A guessed cancel is not final: PayFast's real callback can still follow.
      const recoverable = state.status === 'cancelled' && state.guessed;
      if (!LIVE.has(state.status) && !recoverable) return state;
      const resolved = { guessed: false, unresolvedAttempt: false };
      return event.result
        ? { ...state, ...resolved, status: 'success', supersededPrevious: false }
        : { ...state, ...resolved, status: 'cancelled' };
    }

    case 'PAYFAST_CANCEL':
      return LIVE.has(state.status) ? { ...state, status: 'cancelled', guessed: true } : state;

    case 'TIMEOUT':
      return state.status === 'awaiting_payfast' ? { ...state, status: 'timed_out', guessed: true } : state;

    case 'PRIOR_ATTEMPT_RESOLVED':
      if (event.paid) {
        // Too late to stop PayFast: show this attempt's outcome and warn.
        if (payFastMayBeOpen(state)) {
          return state.earlierPaid ? state : { ...state, earlierPaid: true, supersededPrevious: false };
        }
        // This attempt never reached PayFast (or is idle/closed): the earlier payment is the result.
        return { ...INITIAL_PAYMENT_STATE, status: 'success', plan: event.plan ?? 'payg', amount: event.amount ?? null };
      }
      // While our own window is open the callback in play is this attempt's.
      if (state.status === 'awaiting_payfast') return state;
      if (!state.unresolvedAttempt && !state.supersededPrevious) return state;
      return { ...state, unresolvedAttempt: false, supersededPrevious: false };

    case 'RETURN_QUERY':
      // Only a fresh page load reports a return; never override a live attempt.
      if (state.status !== 'idle') return state;
      return event.payment === 'success'
        ? { ...state, status: 'success', supersededPrevious: false, unresolvedAttempt: false }
        : { ...state, status: 'cancelled', fromReturn: true, unresolvedAttempt: false };

    case 'RESET':
    case 'CLOSE':
      // An unresolved attempt outlives the screen: the next OPEN must warn.
      if (state.unresolvedAttempt) return { ...INITIAL_PAYMENT_STATE, unresolvedAttempt: true };
      return state === INITIAL_PAYMENT_STATE ? state : INITIAL_PAYMENT_STATE;

    default:
      return state;
  }
}

export interface PaymentView {
  message: string;
  actions: PaymentAction[];
}

/** What the customer reads and which buttons they get. Idle shows the pack grid, not a message. */
export function view(state: PaymentState): PaymentView {
  const lead = state.supersededPrevious ? `${PREVIOUS_ATTEMPT_UNFINISHED} ` : '';
  const dup = state.earlierPaid ? `${EARLIER_PAYMENT_ALSO_PAID} ` : '';
  // Once an earlier payment is known to have gone through, a retry would be a third charge.
  const retryable = (actions: PaymentAction[]) => (state.earlierPaid ? actions.filter((a) => a !== 'retry') : actions);
  switch (state.status) {
    case 'idle':
      return { message: '', actions: [] };
    case 'generating':
    case 'engine_loading':
    case 'awaiting_payfast':
      return { message: lead + beforeMessage(state.plan), actions: ['close'] };
    case 'success':
      return { message: dup + successMessage(state.amount, state.plan ?? 'payg'), actions: ['close'] };
    case 'failed':
      return { message: dup + lead + FAILED, actions: retryable(['retry', 'contact_support', 'close']) };
    case 'cancelled':
      // Only PayFast's own cancel redirect proves nothing was charged.
      return {
        message:
          dup + lead + (state.fromReturn ? FAILED : unconfirmedMessage(state.plan, state.guessed ? 'incomplete' : 'cancelled')),
        actions: retryable(['retry', 'contact_support', 'close']),
      };
    case 'timed_out':
      return {
        message: dup + lead + unconfirmedMessage(state.plan, 'incomplete'),
        actions: retryable(['retry', 'contact_support', 'close']),
      };
    case 'rate_limited':
      return { message: lead + rateLimitedMessage(state.retryAfter), actions: ['contact_support', 'close'] };
    case 'error':
      if (state.needsSignIn) return { message: SIGN_IN_AGAIN, actions: ['close'] };
      return {
        message: lead + (state.message ? errorMessage(state.message) : ERROR_FALLBACK),
        actions: ['retry', 'contact_support', 'close'],
      };
  }
}
